import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaXTwitter, FaGithub } from "react-icons/fa6";
import { m, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const Footer: React.FC = () => {
    const currentYear = new Date().getFullYear();

    const sections = [
        {
            title: 'Tools',
            links: [
                { label: 'Text Comparer', path: '/tools/compare-text' },
                { label: 'File Compressor', path: '/tools/compress-pdf-images' },
                { label: 'PDF Merger & Splitter', path: '/tools/merge-split-pdf' },
                { label: 'QR Code Generator', path: '/tools/qr-code-generator' },
                { label: 'Barcode Reader', path: '/tools/barcode-scanner' },
                { label: 'Text Extractor', path: '/tools/scan-image-to-text' },
                { label: 'Background Remover', path: '/tools/remove-background' },
                { label: 'JSON Validator', path: '/tools/json-validator-formatter' },
            ]
        },
        {
            title: 'Developer Hub',
            links: [
                { label: 'Mock API', path: '/dev-hub/mock-api' },
                { label: 'Webhook Tester', path: '/dev-hub/webhook-tester' },
                { label: 'Email Sandbox', path: '/dev-hub/email-sandbox' },
                { label: 'Payment Simulator', path: '/dev-hub/payment-simulator' },
            ]
        },
        {
            title: 'Insights',
            links: [
                { label: 'Articles', path: '/insights/articles' },
                { label: 'Learning Center', path: '/insights/courses' },
            ]
        },
        {
            title: 'Company',
            links: [
                { label: 'About Us', path: '/about' },
                { label: 'Contact Us', path: '/contact' },
                { label: 'Features', path: '/features' },
                { label: 'Help Center', path: '/faq' },
            ]
        }
    ];

    const AccordionItem: React.FC<{ section: typeof sections[0] }> = ({ section }) => {
        const [isOpen, setIsOpen] = useState(false);

        return (
            <div className="w-full border-b border-white/5 last:border-0">
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full !flex !items-center !justify-between py-5 px-0 group transition-colors hover:text-primary"
                >
                    <span className="text-[14px] font-semibold tracking-wide text-foreground/80 group-hover:text-primary transition-colors !text-left !flex-1">
                        {section.title}
                    </span>
                    <m.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                        className="text-foreground/30 group-hover:text-primary ml-auto"
                    >
                        <ChevronDown className="w-4 h-4" />
                    </m.div>
                </button>
                <AnimatePresence initial={false}>
                    {isOpen && (
                        <m.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
                            className="overflow-hidden"
                        >
                            <div className="pb-6 !flex !flex-col gap-4 !pl-12 !items-start !text-left">
                                {section.links.map((link) => (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        className="text-[14px] text-foreground/60 font-medium transition-colors hover:text-primary"
                                    >
                                        {link.label}
                                    </Link>
                                ))}
                            </div>
                        </m.div>
                    )}
                </AnimatePresence>
            </div>
        );
    };

    return (
        <footer className="footer relative w-full bg-background overflow-hidden flex flex-col items-center">
            <div className="w-full max-w-6xl FF-indent z-10 relative flex flex-col gap-4">

                {/* Tailwind Architectural Spacer: Guarantees top breathing room (symmetric with bottom) */}
                <div className="h-4 w-full" />


                {/* Index Level Link Cluster: High-Fidelity Centered Single Row */}
                <div className="w-full">
                    {/* Desktop View: 5-Column Grid */}
                    <div className="hidden lg:flex justify-center gap-x-24 gap-y-16">
                        {/* Column 1: Tools (Part 1) */}
                        <div className="flex flex-col items-start text-left">
                            <h3 className="text-[14px] font-semibold text-foreground border-b-2 border-primary/40 mb-6 inline-block">
                                {sections[0].title}
                            </h3>
                            <div className="flex flex-col items-start gap-3">
                                {sections[0].links.slice(0, 4).map((link) => (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        className="text-[14px] text-foreground/65 font-medium transition-colors hover:text-primary whitespace-nowrap"
                                    >
                                        {link.label}
                                    </Link>
                                ))}
                            </div>
                        </div>

                        {/* Column 2: Tools (Part 2) */}
                        <div className="flex flex-col items-start text-left">
                            <h3 className="text-[14px] font-semibold text-foreground border-b-2 border-transparent mb-6 inline-block opacity-0 select-none">
                                {sections[0].title}
                            </h3>
                            <div className="flex flex-col items-start gap-3">
                                {sections[0].links.slice(4).map((link) => (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        className="text-[14px] text-foreground/65 font-medium transition-colors hover:text-primary whitespace-nowrap"
                                    >
                                        {link.label}
                                    </Link>
                                ))}
                            </div>
                        </div>

                        {/* Subsequent Columns: Dev Hub, Insights, Company */}
                        {sections.slice(1).map((section) => (
                            <div key={section.title} className="flex flex-col items-start text-left">
                                <h3 className="text-[14px] font-semibold text-foreground border-b-2 border-primary/40 mb-6 inline-block">
                                    {section.title}
                                </h3>
                                <div className="flex flex-col items-start gap-3">
                                    {section.links.map((link) => (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            className="text-[14px] text-foreground/65 font-medium transition-colors hover:text-primary whitespace-nowrap"
                                        >
                                            {link.label}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Mobile View: High-Fidelity Accordion Stack */}
                    <div className="lg:!hidden !flex !flex-col w-full !items-start">
                        {sections.map((section) => (
                            <AccordionItem key={section.title} section={section} />
                        ))}
                    </div>
                </div>

                {/* Polished Meta Layer - HubSpot Inspired Centered Stack */}
                <div className="flex flex-col items-center gap-4">

                    {/* Decorative Social Partition: Cinematic Horizontal Lines */}
                    <div className="w-full flex items-center gap-6">
                        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-foreground/5 to-foreground/15" />

                        {/* Centered Socials: Minimalist Borderless Series */}
                        <div className="flex items-center gap-8">
                            <a
                                href="https://github.com/FileFusion-app"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-foreground/25 hover:text-primary transition-all duration-300 hover:scale-110"
                                aria-label="Follow us on GitHub"
                            >
                                <FaGithub className="w-5 h-5" />
                            </a>
                            <a
                                href="https://x.com/FileFusion"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-foreground/25 hover:text-primary transition-all duration-300 hover:scale-110"
                                aria-label="Follow us on X (Twitter)"
                            >
                                <FaXTwitter className="w-5 h-5" />
                            </a>
                        </div>

                        <div className="flex-1 h-px bg-gradient-to-l from-transparent via-foreground/5 to-foreground/15" />
                    </div>

                    {/* Centered Brand & Copyright: 1:1 Header Parity */}
                    <div className="flex flex-col items-center gap-4">
                        <Link
                            to="/"
                            className="flex items-center gap-2 group transition-colors active:scale-[0.98] cursor-pointer"
                        >
                            <img
                                src="/assets/logos/filefusion-icon-only.svg"
                                alt="FileFusion"
                                width={24}
                                height={24}
                                className="w-6 h-6 shrink-0"
                            />
                            <span className="text-lg font-bold font-brand tracking-tight text-foreground transition-colors group-hover:text-primary">
                                FileFusion
                            </span>
                        </Link>
                        <p className="text-[14px] text-foreground/60 font-medium">
                            © {currentYear} FileFusion
                        </p>
                    </div>

                    {/* Centered Legal Row */}
                    <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-[15px] text-foreground/65 font-medium">
                        <Link to="/privacy" className="hover:text-primary transition-colors">
                            Privacy Policy
                        </Link>
                        <span className="hidden md:block w-px h-3 bg-border/20" />
                        <Link to="/terms" className="hover:text-primary transition-colors">
                            Terms of Service
                        </Link>
                        <span className="hidden md:block w-px h-3 bg-border/20" />
                        <a href="/sitemap.xml" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                            Sitemap
                        </a>
                        <span className="hidden md:block w-px h-3 bg-border/20" />
                        <a href="/security.txt" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                            Security
                        </a>
                    </div>

                    {/* Tailwind Architectural Spacer: Guarantees bottom breathing room (symmetric with top) */}
                    <div className="h-4 w-full" />

                </div>
            </div>
        </footer>
    );
};

export default Footer;
